/**
 * File: craService.ts
 * Description: Service class for Context Review and Analysis operations
 * Author: GitHub Copilot
 * Created: 2025-08-25
 * Last Modified: 2025-08-25
 */

import { API_CONFIG } from '../config/api';

import { apiClient } from './apiClient';

export interface CRAAnalysisItem {
  title: string;
  score: number;
  explanation: string;
  details?: string;
}

export interface CRAAnalysisResponse {
  items: CRAAnalysisItem[];
  projectId: number;
  analysisId?: string;
  timestamp?: string;
}

export class CRAService {
  /**
   * Get existing analysis results for a project
   * @param projectId - The project ID
   * @return Promise with analysis results or null if none exist
   * @throws ApiError on request failure
   */
  public async getAnalysis(projectId: number): Promise<CRAAnalysisResponse | null> {
    try {
      return await apiClient.get<CRAAnalysisResponse>(`/api/project/${projectId}/context/review_analysis`);
    } catch {
      // Return null if no existing analysis found (404) or other errors
      return null;
    }
  }

  /**
   * Generate new context review and analysis for a project
   * @param projectId - The project ID to analyze
   * @return Promise with analysis results
   * @throws ApiError on request failure
   */
  public async generateAnalysis(projectId: number): Promise<CRAAnalysisResponse> {
    return apiClient.postWithTimeout<CRAAnalysisResponse>(
      `/api/project/${projectId}/context/review_analysis/generate`,
      undefined,
      API_CONFIG.LONG_RUNNING_TIMEOUT,
    );
  }

  /**
   * Get existing analysis or generate new one if none exists
   * @param projectId - The project ID to analyze
   * @return Promise with analysis results
   * @throws ApiError on request failure
   */
  public async getOrGenerateAnalysis(projectId: number): Promise<CRAAnalysisResponse> {
    // First try to get existing analysis
    const existingAnalysis = await this.getAnalysis(projectId);
    
    if (existingAnalysis?.items?.length && existingAnalysis.items.length > 0) {
      return existingAnalysis;
    }
    
    // If no existing analysis, generate new one
    return this.generateAnalysis(projectId);
  }
}

export const craService = new CRAService();
